import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  CircularProgress, Alert, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, Grid
} from '@mui/material';
import { approvalService } from '../services/approvalService';

const ENTITY_TYPES = ['direct_emission', 'allocation_execution', 'e_liability_transfer', 'embedded_emission'];
const VERIFIER_TYPES = ['human', 'ai_agent', 'automated_rule'];
const ACTIONS = ['approve', 'reject', 'request_revision', 'escalate'];

function ApprovalWorkflows() {
  const [entityType, setEntityType] = useState('direct_emission');
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [form, setForm] = useState({ verifier_name: '', verifier_type: 'human', action: 'approve', reasoning: '', confidence_score: '' });

  useEffect(() => {
    loadPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType]);

  const loadPending = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await approvalService.getPending(entityType);
      setPending(data ?? []);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const pkField = {
        direct_emission: 'direct_emission_id',
        allocation_execution: 'allocation_execution_id',
        e_liability_transfer: 'transfer_id',
        embedded_emission: 'embedded_emission_id'
      }[entityType];

      await approvalService.submit({
        entityType,
        entityId: selectedEntity[pkField],
        verifierName: form.verifier_name,
        verifierType: form.verifier_type,
        action: form.action,
        reasoning: form.reasoning,
        confidenceScore: form.verifier_type === 'ai_agent' ? parseFloat(form.confidence_score) : undefined
      });
      setDialogOpen(false);
      setForm({ verifier_name: '', verifier_type: 'human', action: 'approve', reasoning: '', confidence_score: '' });
      loadPending();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Approval Workflows</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Structured verification decisions for emissions, allocations, and transfers
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <FormControl sx={{ minWidth: 250 }}>
          <InputLabel>Entity Type</InputLabel>
          <Select value={entityType} label="Entity Type" onChange={(e) => setEntityType(e.target.value)}>
            {ENTITY_TYPES.map(t => <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>)}
          </Select>
        </FormControl>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Pending Approvals ({pending.length})</Typography>
          {pending.length === 0 ? (
            <Alert severity="success">No items pending approval for {entityType.replace(/_/g, ' ')}</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pending.map((item, idx) => {
                  const pk = Object.keys(item).find(k => k.endsWith('_id') && k !== 'e_ledger_id');
                  return (
                    <TableRow key={idx}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{item[pk]?.slice(0, 8)}...</TableCell>
                      <TableCell><Chip label={item.verification_status || item.status} size="small" color="warning" /></TableCell>
                      <TableCell>{new Date(item.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Button size="small" variant="contained" onClick={() => { setSelectedEntity(item); setDialogOpen(true); }}>
                          Review
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit Approval Decision</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField fullWidth label="Verifier Name" value={form.verifier_name} onChange={(e) => setForm({ ...form, verifier_name: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Verifier Type</InputLabel>
                <Select value={form.verifier_type} label="Verifier Type" onChange={(e) => setForm({ ...form, verifier_type: e.target.value })}>
                  {VERIFIER_TYPES.map(t => <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Action</InputLabel>
                <Select value={form.action} label="Action" onChange={(e) => setForm({ ...form, action: e.target.value })}>
                  {ACTIONS.map(a => <MenuItem key={a} value={a}>{a.replace(/_/g, ' ')}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            {form.verifier_type === 'ai_agent' && (
              <Grid item xs={6}>
                <TextField fullWidth label="Confidence Score (0-1)" type="number" inputProps={{ step: 0.01, min: 0, max: 1 }}
                  value={form.confidence_score} onChange={(e) => setForm({ ...form, confidence_score: e.target.value })} />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Reasoning (required)" value={form.reasoning}
                onChange={(e) => setForm({ ...form, reasoning: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!form.verifier_name || !form.reasoning}>Submit</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ApprovalWorkflows;
